import { Client } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { queryOptionsGetClientsPaginated } from '../api/workspaces/clientApi';
import { CLIENT_SEARCH } from '../constant/Common';
import { capitalizeWords } from '../utils/helpers/common';
import AppSearchField from './AppSearchField';
import AppTableV2, { AppTableV2HeaderCell as TH } from './AppTableV2';
import IfFeatureEnabled from './IfFeatureEnabled';

// Stable reference so a still-loading (or absent) query doesn't feed the
// `.map` below a fresh `[]` on every render.
const NO_CLIENTS: Client[] = [];

const PAGINATION_KEY = 'clientsTablePage';
const DEFAULT_SORT = 'clientName:asc';

interface IClientsTableProps {
  isStaffView?: boolean;
  workspaceId?: string;
}

const ClientsTable = ({
  isStaffView = false,
  workspaceId,
}: IClientsTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Page and sort come from the URL, the same values AppTableV2 reads for
  // Prev/Next and the column header, so the request can never disagree with
  // what the table shows.
  const currentPage = Number(searchParams.get(PAGINATION_KEY)) || 1;
  const currentSort = searchParams.get('sortBy') ?? DEFAULT_SORT;

  const clientSearchValue = useMemo(() => {
    if (isStaffView) return undefined;

    return searchParams.get(CLIENT_SEARCH) ?? undefined;
  }, [searchParams, isStaffView]);

  // AppTableV2 only enables Prev/Next when `onPageRequest` is set. The query
  // reads page and sort from the URL, so there is nothing to store here.
  const handlePageRequest = useCallback(() => {
    /* intentionally empty */
  }, []);

  // Writes the term and drops the page in one URL update, so a new search
  // starts from page 1 and no request goes out for the old page.
  const commitSearch = useCallback(
    (searchValue: string) => {
      setSearchParams(
        (current) => {
          if (searchValue) {
            current.set(CLIENT_SEARCH, searchValue);
          } else {
            current.delete(CLIENT_SEARCH);
          }
          current.delete(PAGINATION_KEY);

          return current;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const {
    data: paginatedData,
    isLoading,
    isPlaceholderData,
  } = useQuery(
    queryOptionsGetClientsPaginated({
      clientName: clientSearchValue,
      page: currentPage,
      pageSize: 25,
      sortBy: currentSort,
      workspaceId,
    }),
  );

  const clients = paginatedData?.items ?? NO_CLIENTS;
  const paginationMeta = paginatedData?.meta;
  // `isLoading` (TanStack's `isPending && isFetching`) covers the first load
  // and, unlike raw `isPending`, correctly stays `false` when the query is
  // skipped (no `workspaceId` yet) rather than spinning forever.
  // `isPlaceholderData` covers in-between loading: `keepPreviousData` (set in
  // `queryOptionsGetClientsPaginated`) keeps the previous page's rows visible
  // while a page/sort/search change is in flight, so this is what drives the
  // overlay + disabled pagination for that window instead of a content flash.
  const isTableLoading = isLoading || isPlaceholderData;

  return (
    <AppTableV2
      id="clients-table"
      customEmptyTable={{
        description: clientSearchValue ? (
          <p className="text-base">
            We couldn&apos;t find what you are looking for.
          </p>
        ) : (
          <p className="text-base">
            Please contact{' '}
            <a
              className="font-semibold underline"
              href="mailto:support@screenrights.org"
            >
              support@screenrights.org
            </a>
            .
          </p>
        ),
        icon: 'person',
        title: clientSearchValue
          ? 'No clients found'
          : "Looks like you don't have any clients yet.",
      }}
      filterElement={
        isStaffView ? undefined : (
          <IfFeatureEnabled name="customer-client-search">
            <AppSearchField
              commitSearch={commitSearch}
              placeHolder="Search Clients"
              searchKey={CLIENT_SEARCH}
              width="w-96"
            />
          </IfFeatureEnabled>
        )
      }
      hasBottomNavigation={true}
      headerRowElement={
        <tr>
          <TH
            sortByProperty={isStaffView ? undefined : 'clientName'}
            sortOrderByProperty="desc"
          >
            Client Name
          </TH>
          <TH sortByProperty={isStaffView ? undefined : 'representationStatus'}>
            Status
          </TH>
        </tr>
      }
      isLoading={isTableLoading}
      paginationKey={PAGINATION_KEY}
      paginationMeta={paginationMeta}
      onPageRequest={handlePageRequest}
    >
      {clients?.map((client) => (
        <tr key={client._id}>
          <td className="break-words">{client.clientName}</td>
          <td className="break-words">
            {capitalizeWords(client.representationStatus)}
          </td>
        </tr>
      ))}
    </AppTableV2>
  );
};

export default ClientsTable;
